export const checkIsChildPlaying = (item, id) => {
  if (item.id === id) {
    return true;
  }
  const { children } = item;
  if (children && children.length) {
    let result = false;
    for (let i = 0; i < children.length; i++) {
      result = checkIsChildPlaying(children[i], id);
      if (result) {
        break;
      }
    }
    return result;
  }
  return false;
};

export const getFileDuration = (mp3Url) => {
  if (!mp3Url) {
    return 0;
  }
  let fileDuration = 0;
  const durationReg = /duration=(.*)&/;
  const [, value] = mp3Url.match(durationReg);
  if (value) {
    fileDuration = parseInt(value, 10);
  }
  return fileDuration;
};

export const getFileSize = (mp3Url) => {
  if (!mp3Url) {
    return 0;
  }
  let size = 0;
  const regExp = /size=(.*)$/;
  const [, value] = mp3Url.match(regExp);
  if (value) {
    size = parseInt(value, 10);
  }
  return size;
};

export const findWaveformsDictionaryParaById = (waveformsDictionary, targetId, paraId) => {
  if (!targetId || !waveformsDictionary[targetId]) {
    return null;
  }

  return waveformsDictionary[targetId].paragraphsAudioPositions.find((item) => {
    return item.id === paraId;
  });
};

export const findWaveformsDictionaryParaByPosition = (waveformsDictionary, targetId, position) => {
  if (!waveformsDictionary[targetId]) {
    return null;
  }

  return waveformsDictionary[targetId].paragraphsAudioPositions.find((item) => {
    return item.start <= position && item.end > position;
  });
};

const getParentAudioChapter = (chapters, chapter) => {
  if (!chapter || chapter.level === 1) {
    return null;
  }

  const parentLevel = chapter.level - 1;
  const index = chapters.findIndex((item) => item === chapter);

  let possibleParentChapter;
  for (let i = index - 1; i >= 0; i--) {
    possibleParentChapter = chapters[i];
    if (possibleParentChapter.level === parentLevel) {
      if (possibleParentChapter.mp3) {
        return possibleParentChapter;
      }

      return getParentAudioChapter();
    }
  }

  return null;
};

const isChapterDuplicate = (chapter1, chapter2) => {
  if (!chapter1 || !chapter2) {
    return false;
  }

  return (chapter1.dup && chapter1.dup === chapter2.id)
    || (chapter2.dup && chapter2.dup === chapter1.id);
};

const getDuplicateChapters = (toc, chapter, out = []) => {
  if (toc && chapter) {
    toc
      .filter((c) => isChapterDuplicate(c, chapter))
      .forEach((dc) => {
        const isInOut = out.includes(dc);

        if (!isInOut) {
          out.push(dc);
          getDuplicateChapters(toc, dc, out);
        }
      });
  }

  return out;
};

export const _testGetDuplicateChapters = getDuplicateChapters;

export const getMaleAudio = (audioChapter) => ({...audioChapter, mp3: audioChapter.mp3.replace('_f_', '_m_')})
const normalizeAudioChapter = (audioChapter) => {
  if (audioChapter && audioChapter.id.split('.')[0] === '108') {
    // [EGWW-4277] Steps to Christ 
    return getMaleAudio(audioChapter)
  }

  return audioChapter
}

export const getAudioChapterByChapterId = (toc, chapterId) => {
  if (!chapterId || !toc || !toc.length) {
    return null;
  }

  // Case 1 - Basic.
  const currentChapter = toc.find((chapter) => chapter.id === chapterId);

  if (currentChapter && currentChapter.mp3) {
    return normalizeAudioChapter(currentChapter);
  }
  // ===================================

  // Case 2 - Duplicates.
  // e.g. 1965.2 is a dup of 1965.1 that is a dup of 1965.3
  const duplicateChapters = getDuplicateChapters(toc, currentChapter);
  const duplicateChapterWithMP3 = duplicateChapters.find((c) => !!c.mp3);
  // ============================================

  return normalizeAudioChapter(duplicateChapterWithMP3 || getParentAudioChapter(toc, currentChapter));
};

export const getFirstAvailableAudioChapter = (toc) => {
  if (!toc) {
    return null;
  }

  return toc.find((item) => item.mp3);
};

/**
 *
 * @typedef {Object} SeekChapter
 * @property {{} | null} audioChapter
 * @property {{} | null} chapter
 * @property {{} | null} positions
 * @property {boolean} isMissingWaveform
 */

export const isChapterInPlayer = (
  toc,
  idChapter,
  waveformsDictionary,
  idAudioChapterPlayer,
  playerPosition,
) => {
  const indexChapter = toc.findIndex((c) => c.id === idChapter);
  const chapter = toc[indexChapter];
  const {id} = chapter;

  const audioChapter = getAudioChapterByChapterId(toc, id);

  let audioPositions = null;
  let isInPlayer = false;
  if (audioChapter && audioChapter.id === idAudioChapterPlayer) {
    let isOKPrev = true, isOKNext = true;

    const chapterPrev = toc[indexChapter - 1];

    if (chapterPrev) {
      const audioChapterPrev = getAudioChapterByChapterId(toc, chapterPrev.id);

      if (audioChapterPrev && audioChapterPrev.mp3 === audioChapter.mp3) {
        const audioPositionsPrev = findWaveformsDictionaryParaById(
          waveformsDictionary, audioChapterPrev.id, chapterPrev.id,
        );

        if (audioPositionsPrev) {
          isOKPrev = playerPosition >= audioPositionsPrev.end;
        }
      }
    }

    const chapterNext = toc[indexChapter + 1];

    if (chapterNext) {
      const audioChapterNext = getAudioChapterByChapterId(toc, chapterNext.id);

      if (audioChapterNext && audioChapterNext.mp3 === audioChapter.mp3) {
        const audioPositionsNext = findWaveformsDictionaryParaById(
          waveformsDictionary, audioChapterNext.id, chapterNext.id,
        );

        if (audioPositionsNext) {
          isOKNext = playerPosition < audioPositionsNext.start;
        }
      }
    }

    isInPlayer = isOKPrev && isOKNext;
  }

  return {isInPlayer, audioChapter, audioPositions};
};
/**
 * @returns {SeekChapter | null}
 */
export const getSeekChapter = (
  toc, indexChapterPlaying, waveformsDictionary, isForward,
) => {
  let index = indexChapterPlaying + (isForward ? 1 : -1);
  let chapter = toc[index];

  while (chapter) {
    const audioChapter = getAudioChapterByChapterId(toc, chapter.id);

    if (audioChapter) {
      const positions = findWaveformsDictionaryParaById(
        waveformsDictionary, audioChapter.id, chapter.id,
      );

      return {
        audioChapter,
        chapter,
        positions,
        isMissingWaveform: !waveformsDictionary[audioChapter.id],
      };
    }

    index += isForward ? 1 : -1;
    chapter = toc[index];
  }

  return null;
};

export const filterTOCToAudioChapters = (toc) => {
  if (!toc) {
    return null;
  }

  return toc.filter((chapter) => chapter.mp3);
};
